import { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { DAYS_OF_WEEK, type DayOfWeek } from "@shared/types/foyer";
import type {
  OfferAvailability,
  OfferOverridePatch,
  ResolvedRoomOffer,
  RoomOffer,
} from "@shared/types/roomOffer";
import { offerOverrideProblems } from "@shared/roomOfferLayers";
import { readLocalized } from "@shared/i18n";
import AssetField from "../AssetField";
import { tokens } from "../../theme";
import { offerProblemMessage, type OfferProblem } from "./offerCopy";

/**
 * Re-decide one inherited offer for THIS hotel (`POS-14`).
 *
 * ## Why this is not the offer editor with the fields greyed out
 *
 * Because the two dialogs answer different questions. `OfferDialog` asks "what
 * is this offer?"; this one asks "what is different about it HERE?" — and the
 * difference is the whole record. Every control below is a three-state one:
 *
 * | state | what it means | what is stored |
 * |---|---|---|
 * | **Inherit** | whatever the chain says, now and after the chain edits it | the key is ABSENT |
 * | **Free / no picture** | this property decided there is none | the key is `null` |
 * | a value | this property decided this one | the value |
 *
 * Absent and null being different is not pedantry: collapsing them would make
 * "free at this hotel" impossible to express, and the operator would express it
 * by COPYING the offer instead — which silently stops tracking the chain
 * original, and nobody notices until the chain edits and one hotel does not
 * change. That copy is the failure this dialog exists to prevent, so the
 * inherit state is the default on every field and it is spelled out on screen.
 *
 * Title, description and the pick list are deliberately absent. A property that
 * could re-write those would be authoring its own offer with extra steps, and
 * the chain would have no idea what its campaign says in that building.
 */

export interface OfferOverrideDialogProps {
  /** The row as this property currently sees it — patch already applied. */
  offer: ResolvedRoomOffer;
  /**
   * What the chain (or Nenda) actually authored — the UNPATCHED row.
   *
   * Passed separately so every control can say what it is inheriting. A dialog
   * that only knew the patched row could not tell an operator what they would
   * go back to, which is the one thing they are deciding about.
   */
  original: RoomOffer;
  saving?: boolean;
  error?: string | null;
  serverProblems?: OfferProblem[];
  onClose: () => void;
  onSave: (patch: OfferOverridePatch) => void;
  /** Offered only when a delta already exists. */
  onRevert?: () => void;
}

type PriceMode = "inherit" | "free" | "custom";

const DAY_LABEL: Record<DayOfWeek, string> = {
  mon: "Mon",
  tue: "Tue",
  wed: "Wed",
  thu: "Thu",
  fri: "Fri",
  sat: "Sat",
  sun: "Sun",
};

function priceOf(offer: RoomOffer) {
  return "price" in offer.action ? offer.action.price : undefined;
}

/** What the chain charges, as one line for the hint under the control. */
function inheritedPriceLabel(original: RoomOffer): string {
  const price = priceOf(original);
  if (!("price" in original.action)) return "this kind has no price";
  return price ? `${price.amount} ${price.currency}` : "free";
}

function windowLabel(a: OfferAvailability): string {
  const parts: string[] = [];
  if (a.startDate || a.endDate) {
    parts.push(`${a.startDate ?? "…"} – ${a.endDate ?? "…"}`);
  }
  if (a.startTime || a.endTime) {
    parts.push(`${a.startTime ?? "00:00"}–${a.endTime ?? "00:00"}`);
  }
  if (a.days && a.days.length > 0) {
    parts.push(a.days.map((d) => DAY_LABEL[d]).join(" "));
  }
  return parts.length > 0 ? parts.join(" · ") : "always on";
}

export default function OfferOverrideDialog({
  offer,
  original,
  saving,
  error,
  serverProblems,
  onClose,
  onSave,
  onRevert,
}: OfferOverrideDialogProps) {
  const fields = offer.overriddenFields;
  const kindHasPrice = original.action.kind !== "promotion";

  const [priceMode, setPriceMode] = useState<PriceMode>(() => {
    if (!fields.includes("price")) return "inherit";
    return priceOf(offer) ? "custom" : "free";
  });
  const [amount, setAmount] = useState<string>(() =>
    priceOf(offer) ? String(priceOf(offer)?.amount) : ""
  );
  const [currency, setCurrency] = useState<string>(
    () => priceOf(offer)?.currency || priceOf(original)?.currency || "kr"
  );

  const [ownWindow, setOwnWindow] = useState(fields.includes("availability"));
  const [availability, setAvailability] = useState<OfferAvailability>(
    () => offer.availability ?? {}
  );

  const [ownImage, setOwnImage] = useState(fields.includes("imageUrl"));
  const [imageUrl, setImageUrl] = useState(offer.imageUrl ?? "");

  const patch = useMemo<OfferOverridePatch>(() => {
    const next: OfferOverridePatch = {};
    if (kindHasPrice && priceMode !== "inherit") {
      next.price =
        priceMode === "free"
          ? null
          : {
              // A BLANK amount is NaN, never 0. `Number("")` is zero, and a
              // zero saved here would put a "0 kr" line into the revenue
              // series — §2's rule, and the same one `OfferDialog` states.
              // Free is its own choice above; an empty box is an unfinished
              // one, and NaN is what makes `moneyProblems()` say so and hold
              // the Save button.
              amount: amount.trim() === "" ? Number.NaN : Number(amount.trim()),
              currency: currency.trim(),
            };
    }
    if (ownWindow) next.availability = availability;
    if (ownImage) next.imageUrl = imageUrl.trim() === "" ? null : imageUrl;
    return next;
  }, [
    kindHasPrice,
    priceMode,
    amount,
    currency,
    ownWindow,
    availability,
    ownImage,
    imageUrl,
  ]);

  // Checked here as well as on the server, with the SAME function, so a bad
  // amount is caught before a round trip rather than only after one.
  const problems = useMemo(
    () => [
      ...offerOverrideProblems(patch).map(offerProblemMessage),
      ...(serverProblems ?? []),
    ],
    [patch, serverProblems]
  );
  const forField = (field: OfferProblem["field"]) =>
    problems.find((p) => p.field === field)?.message;

  const setWindow = (part: Partial<OfferAvailability>) =>
    setAvailability((prev) => ({ ...prev, ...part }));
  const text = (v: string) => (v.trim() === "" ? undefined : v);

  return (
    <Dialog open fullWidth maxWidth="sm" onClose={onClose}>
      <DialogTitle sx={{ pb: 0.5 }}>
        Override for this location
        <Typography sx={{ fontSize: 13, color: tokens.inkMuted, mt: 0.25 }}>
          {readLocalized(original.title, "sv") || "Untitled"} —{" "}
          {offer.origin === "company" ? "from the chain" : "from Nenda"}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Alert severity="info" sx={{ mt: 1, mb: 2 }}>
          Anything left on <strong>Inherit</strong> keeps following the
          original. Change the chain’s price next month and this location
          follows it — for every field it has not claimed here.
        </Alert>

        <Stack spacing={2.5}>
          {kindHasPrice && (
            <Box>
              <Stack direction="row" spacing={1}>
                <TextField
                  select
                  size="small"
                  label="Price here"
                  value={priceMode}
                  onChange={(e) => setPriceMode(e.target.value as PriceMode)}
                  sx={{ width: 200, flex: "none" }}
                  inputProps={{ "data-testid": "override-price-mode" }}
                >
                  <MenuItem value="inherit">
                    Inherit ({inheritedPriceLabel(original)})
                  </MenuItem>
                  {/* Free is its own choice, never "0". A zero would put a
                      0 kr line into the revenue series — §2. */}
                  <MenuItem value="free">Free here</MenuItem>
                  <MenuItem value="custom">A different price</MenuItem>
                </TextField>
                {priceMode === "custom" && (
                  <>
                    <TextField
                      size="small"
                      label="Price"
                      type="number"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            {currency}
                          </InputAdornment>
                        ),
                      }}
                      inputProps={{ min: 0, "data-testid": "override-price" }}
                      error={Boolean(forField("price"))}
                      sx={{ flex: 1 }}
                    />
                    <TextField
                      size="small"
                      label="Currency"
                      value={currency}
                      onChange={(e) => setCurrency(e.target.value)}
                      inputProps={{ maxLength: 8 }}
                      sx={{ width: 96, flex: "none" }}
                    />
                  </>
                )}
              </Stack>
              {forField("price") && (
                <Typography
                  role="alert"
                  sx={{ mt: 0.75, fontSize: 12.5, color: tokens.error }}
                >
                  {forField("price")}
                </Typography>
              )}
            </Box>
          )}

          <Box>
            <TextField
              select
              size="small"
              label="When it shows here"
              value={ownWindow ? "own" : "inherit"}
              onChange={(e) => setOwnWindow(e.target.value === "own")}
              fullWidth
              inputProps={{ "data-testid": "override-window-mode" }}
              helperText={
                ownWindow
                  ? "Replaces the inherited window entirely — an empty form means always on."
                  : `Inherited: ${windowLabel(original.availability ?? {})}`
              }
            >
              <MenuItem value="inherit">Inherit the chain’s dates</MenuItem>
              <MenuItem value="own">Different dates and times here</MenuItem>
            </TextField>
            {ownWindow && (
              <Stack spacing={1.5} sx={{ mt: 1.5 }}>
                <Stack direction="row" spacing={1}>
                  <TextField
                    size="small"
                    label="From"
                    type="date"
                    value={availability.startDate ?? ""}
                    onChange={(e) =>
                      setWindow({ startDate: text(e.target.value) })
                    }
                    InputLabelProps={{ shrink: true }}
                    sx={{ flex: 1 }}
                    inputProps={{ "data-testid": "override-window-start-date" }}
                  />
                  <TextField
                    size="small"
                    label="Until"
                    type="date"
                    value={availability.endDate ?? ""}
                    onChange={(e) =>
                      setWindow({ endDate: text(e.target.value) })
                    }
                    InputLabelProps={{ shrink: true }}
                    sx={{ flex: 1 }}
                    inputProps={{ "data-testid": "override-window-end-date" }}
                  />
                </Stack>
                <Stack direction="row" spacing={1}>
                  <TextField
                    size="small"
                    label="Shows from"
                    type="time"
                    value={availability.startTime ?? ""}
                    onChange={(e) =>
                      setWindow({ startTime: text(e.target.value) })
                    }
                    InputLabelProps={{ shrink: true }}
                    sx={{ flex: 1 }}
                  />
                  <TextField
                    size="small"
                    label="Shows until"
                    type="time"
                    value={availability.endTime ?? ""}
                    onChange={(e) =>
                      setWindow({ endTime: text(e.target.value) })
                    }
                    InputLabelProps={{ shrink: true }}
                    sx={{ flex: 1 }}
                  />
                </Stack>
                <Box>
                  <Typography
                    sx={{ fontSize: 12.5, color: tokens.inkMuted, mb: 0.5 }}
                  >
                    Days it shows — none selected means every day.
                  </Typography>
                  <Stack direction="row" gap={0.75} flexWrap="wrap">
                    {DAYS_OF_WEEK.map((day) => {
                      const on = (availability.days ?? []).includes(day);
                      return (
                        <Button
                          key={day}
                          size="small"
                          variant={on ? "contained" : "outlined"}
                          onClick={() => {
                            const days = new Set(availability.days ?? []);
                            if (on) days.delete(day);
                            else days.add(day);
                            const next = DAYS_OF_WEEK.filter((d) =>
                              days.has(d)
                            );
                            setWindow({
                              days: next.length > 0 ? next : undefined,
                            });
                          }}
                          sx={{ minWidth: 46, px: 0 }}
                        >
                          {DAY_LABEL[day]}
                        </Button>
                      );
                    })}
                  </Stack>
                </Box>
                {forField("availability") && (
                  <Typography
                    role="alert"
                    sx={{ fontSize: 12.5, color: tokens.error }}
                  >
                    {forField("availability")}
                  </Typography>
                )}
              </Stack>
            )}
          </Box>

          <Box>
            <TextField
              select
              size="small"
              label="Picture here"
              value={ownImage ? "own" : "inherit"}
              onChange={(e) => setOwnImage(e.target.value === "own")}
              fullWidth
              helperText={
                ownImage
                  ? "Leave it empty to show no picture at this location."
                  : original.imageUrl
                    ? "Inherited: the chain’s picture."
                    : "Inherited: no picture."
              }
            >
              <MenuItem value="inherit">Inherit the chain’s picture</MenuItem>
              <MenuItem value="own">A different picture here</MenuItem>
            </TextField>
            {ownImage && (
              <Box sx={{ mt: 1.5 }}>
                <AssetField
                  label="Picture"
                  value={imageUrl}
                  preview
                  onChange={(v) => setImageUrl(v)}
                  testId="override-image"
                />
                {forField("image") && (
                  <Typography
                    role="alert"
                    sx={{ mt: 0.75, fontSize: 12.5, color: tokens.error }}
                  >
                    {forField("image")}
                  </Typography>
                )}
              </Box>
            )}
          </Box>

          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        {onRevert && (
          <Button
            color="inherit"
            onClick={onRevert}
            disabled={saving}
            sx={{ mr: "auto" }}
            data-testid="override-revert"
          >
            Stop overriding
          </Button>
        )}
        <Button color="inherit" onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => onSave(patch)}
          disabled={saving || problems.length > 0}
          data-testid="override-save"
        >
          Save for this location
        </Button>
      </DialogActions>
    </Dialog>
  );
}
